import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Bell, X, Check } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useVisualViewport } from "../lib/useVisualViewport";
import { useAuth } from "../context/AuthContext";

// Compact job-alert control: a small bell near the search tools that opens a clean
// panel with the current search, location and filters already selected. Logged-in
// candidates confirm with their own email (always shown, never hidden); visitors
// use the double opt-in email flow (honeypot, timing and rate limits server-side).
// Never a popup; opening or closing the panel never moves the page.
export function JobAlertControl({ query = "", location = "", category = "", jobType = "", workplace = "",
                                  label = "Job alerts", compact = false, idSuffix = "", className = "" }) {
  const { user } = useAuth();
  const isCandidate = user?.role === "candidate";
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  const [error, setError] = useState("");
  const [website, setWebsite] = useState(""); // honeypot — humans never fill this
  const [startedAt, setStartedAt] = useState(0);
  const [hasAlert, setHasAlert] = useState(false);
  const wrapRef = useRef(null);

  // subtle filled state when the current search already has an active alert
  useEffect(() => {
    if (!isCandidate) { setHasAlert(false); return; }
    api.get("/candidate/searches").then((r) => {
      const match = (r.data || []).some((s) => s.alerts_on
        && (s.query || "") === query && (s.location || "") === location
        && (s.category || "") === category && (s.job_type || "") === jobType
        && (s.workplace || "") === workplace);
      setHasAlert(match);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, query, location, category, jobType, workplace]);

  // focused overlay: Escape or the dimmed backdrop closes it; while open the page behind
  // is scroll-locked, and it's restored exactly on close
  useEffect(() => {
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    const openReq = () => { setEmail(user?.email || ""); setStartedAt(Date.now()); setDone(""); setError(""); setOpen(true); };
    document.addEventListener("keydown", esc);
    window.addEventListener("ht:open-job-alerts", openReq);
    return () => {
      document.removeEventListener("keydown", esc);
      window.removeEventListener("ht:open-job-alerts", openReq);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  useLayoutEffect(() => {
    if (!open) return;
    const html = document.documentElement;
    const prev = html.style.overflow;
    html.style.overflow = "hidden";
    return () => { html.style.overflow = prev; };
  }, [open]);

  // pins the mobile bottom sheet above the on-screen keyboard (no zoom, no shifting)
  const vv = useVisualViewport(open);

  if (user && !isCandidate) return null;

  const toggle = () => {
    if (!open) { setEmail(user?.email || ""); setStartedAt(Date.now()); setDone(""); setError(""); }
    setOpen(!open);
  };

  const what = [query || category, location].filter(Boolean).join(" in ");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (isCandidate) {
        const r = await api.post("/candidate/searches", {
          query, location, category, job_type: jobType, workplace, alerts_on: true,
        });
        setDone(r.data?.already_exists ? "exists" : "saved");
        setHasAlert(true);
      } else {
        await api.post("/alerts/guest", { email, query, location, category, website, form_started_at: startedAt });
        setDone("guest");
      }
    } catch (err) {
      setError(formatApiError(err, "We couldn't save that alert. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={wrapRef} className={className || "relative inline-block order-2 sm:order-none"} data-testid={`job-alert-control${idSuffix}`}>
      <button type="button" onClick={toggle} data-testid={`job-alerts-bell${idSuffix}`} aria-expanded={open}
        className={`inline-flex items-center gap-1.5 h-8 ${compact ? "px-2.5" : "px-3"} border border-zinc-300 rounded-md text-xs font-semibold text-zinc-700 bg-white hover:border-zinc-500 transition-colors duration-200`}>
        <Bell className={`h-3.5 w-3.5 shrink-0 ${hasAlert ? "fill-zinc-700" : ""}`} />
        {compact ? <span className="max-[359px]:hidden">{label}</span> : label}
        {hasAlert && <span className="h-1.5 w-1.5 rounded-full bg-green-600" data-testid={`job-alerts-active-dot${idSuffix}`} />}
      </button>
      {open && (
        <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center sm:p-6"
          style={vv ? { height: vv.height, top: vv.top, bottom: "auto" } : undefined}
          data-testid="job-alert-overlay">
          <button type="button" aria-label="Close" data-testid="job-alert-backdrop" onClick={() => setOpen(false)}
            className="absolute inset-0 bg-zinc-950/35 backdrop-blur-sm" />
          <div data-testid="job-alert-panel" role="dialog" aria-modal="true" aria-label="Job alert setup"
            className="relative w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-xl border border-zinc-200/80 shadow-2xl p-5 text-left max-h-[82vh] overflow-y-auto overscroll-contain">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-bold text-zinc-950 flex items-center gap-1.5">
              <Bell className="h-4 w-4 text-zinc-700" /> New jobs by email
            </p>
            <button type="button" onClick={() => setOpen(false)} data-testid="job-alert-close" aria-label="Close"
              className="p-1.5 text-zinc-400 hover:text-zinc-700 transition-colors duration-200">
              <X className="h-4 w-4" />
            </button>
          </div>
          <p className="text-xs text-zinc-500 mb-3" data-testid="job-alert-context">
            {what ? <>Alerts for <strong>{what}</strong>{jobType ? ` · ${jobType}` : ""}{workplace ? ` · ${workplace}` : ""}.</>
                  : "Alerts for all new roles."}
          </p>
          {done ? (
            <p className="text-sm text-green-700 flex items-start gap-1.5" data-testid="job-alert-success">
              <Check className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                {done === "saved" && <>Done — new matching roles will be emailed to <strong>{user.email}</strong>. Pause or delete any time in your dashboard.</>}
                {done === "exists" && <>You're already getting these alerts at <strong>{user.email}</strong>.</>}
                {done === "guest" && "Almost done — we've emailed you a confirmation link. Click it once and your alerts are on. One-click unsubscribe in every email. If it doesn't arrive within a few minutes, check spam/junk, or close this and submit again to resend a fresh link."}
              </span>
            </p>
          ) : (
            <form onSubmit={submit} data-testid="job-alert-form" className="space-y-2.5">
              {isCandidate ? (
                <p className="text-xs text-zinc-600" data-testid="job-alert-candidate-email">
                  Alerts go to your account email: <strong>{user.email}</strong>
                </p>
              ) : (
                <>
                  <div aria-hidden="true" style={{ position: "absolute", left: "-9999px" }}>
                    <label htmlFor="ja-website">Website</label>
                    <input id="ja-website" type="text" tabIndex={-1} autoComplete="off" value={website}
                      onChange={(e) => setWebsite(e.target.value)} data-testid="job-alert-hp" />
                  </div>
                  <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com" data-testid="job-alert-email"
                    className="w-full h-10 px-3 border border-zinc-300 rounded-md text-base bg-white focus:outline-none focus:ring-2 focus:ring-blue-600" />
                  <p className="text-[11px] text-zinc-400">We email you one confirmation link first — nothing is sent until you confirm.</p>
                </>
              )}
              <button type="submit" disabled={busy || (!isCandidate && !email.trim())} data-testid="job-alert-submit"
                className="w-full h-10 bg-zinc-950 text-white text-sm font-semibold rounded-md hover:bg-zinc-800 transition-colors duration-200 disabled:opacity-50">
                {busy ? "Saving…" : isCandidate ? `Email new matches to ${user.email}` : "Send my confirmation link"}
              </button>
              {error && <p className="text-xs text-red-600" data-testid="job-alert-error">{error}</p>}
            </form>
          )}
          </div>
        </div>
      )}
    </div>
  );
}

  // One-line suggestion variant is retired — the bell in the toolbar is the single,
  // permanent entry point. JobAlertStrip kept as a no-op shim so old imports never break.
export function JobAlertStrip() {
  return null;
}
